import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { Subject, takeUntil, debounceTime } from 'rxjs';
import { CdkDragDrop, CDK_DRAG_CONFIG } from '@angular/cdk/drag-drop';
import { ConfirmationService } from 'primeng/api';
import { WorkbookList } from '../models/workspace.models';
import { WBPermissionType } from '../enum/workbook-permission.enum';
import { DynamicTableService } from 'src/app/modules/shared/services/dynamic-table/dynamic-table.service';
import { AppMainService } from 'src/app/app-main.service';
import { MessageService } from 'primeng/api';
import { WorkbookStateService } from 'src/app/modules/shared/services/workbook-state.service';
import { 
    KanbanViewResponse, 
    KanbanViewResult, 
    KanbanGroup, 
    KanbanRow, 
    KanbanCell,
    KanbanViewQuery 
} from '../models/kanban-view.models';
import { StepBarResponse } from '../modals/stepbar.models';
import { ColumnDefinition } from 'src/app/modules/shared/models/dynamic-table/dynamic-table-data.model';

// Configure drag and drop behavior
const DragConfig = {
  dragStartThreshold: 0,
  pointerDirectionChangeThreshold: 5,
  zIndex: 1000,
  lockAxis: 'x',
  constrainPosition: (point: { x: number; y: number }) => {
    return {
      x: Math.max(0, Math.min(window.innerWidth, point.x)),
      y: Math.max(0, Math.min(window.innerHeight, point.y))
    };
  }
};

@Component({
  selector: 'app-workbook-kanban-view',
  templateUrl: './workbook-kanban-view.component.html',
  styleUrls: ['./workbook-kanban-view.component.scss'],
  providers: [
    { provide: CDK_DRAG_CONFIG, useValue: DragConfig },
    ConfirmationService
  ],
  host: {
    '(touchstart)': 'onTouchStart($event)',
    '(touchmove)': 'onTouchMove($event)',
    '(touchend)': 'onTouchEnd($event)'
  }
})
export class WorkbookKanbanViewComponent implements OnInit, OnDestroy {
  @Input() workbookId: string;
  @Input() kanbanViewColumns: ColumnDefinition[] = [];
  @Input() columnId: string;
  @Input() columnName: string;
  @Input() selectedWorkbook: WorkbookList;
  @Input() selectedGroupByColumn: any;
  @Input() steps: StepBarResponse[] = [];

  @Output() rowSelected = new EventEmitter<any>();
  @Output() cardMoved = new EventEmitter<{card: any, newGroup: string, sourceGroup: string, targetStep: StepBarResponse | undefined}>();

  protected ngUnsubscribe$ : Subject<void> = new Subject<void>();

  kanbanGroupByColumn: string = '';
  kanbanCardTitleColumn: string = '';
  kanbanCardContentColumns: any[] = [];
  selectedRow: KanbanRow | null = null;
  WBPermissionType = WBPermissionType;
  isGrouped: boolean = false;
  list: KanbanGroup[] = [];
  isLoading: boolean = false;
  hasMoreData: boolean = true;
  hasNextPage: boolean = true;
  enabledKanbanViewColumns: ColumnDefinition[] = [];

  // Getter for connected drop lists
  get connectedDropLists(): string[] {
    return this.list.map(col => col.GroupValue);
  }

  // Add new properties for group-specific pagination
  private groupPaginationMap: Map<string, { 
    pageNumber: number, 
    hasMore: boolean,
    totalCount: number,
    loadedCount: number 
  }> = new Map();
  private isLoadingGroup: Map<string, boolean> = new Map();
  private scrollSubject = new Subject<{event: any, groupValue: string}>();

  // Add new property to track last scroll position
  private lastScrollPositions: Map<string, number> = new Map();

  // Add new property for horizontal scroll tracking
  private lastHorizontalScrollPosition: number = 0;
  private horizontalScrollDebounce = new Subject<number>();

  // Add new properties for file and user handling
  displayUserDetails: boolean = false;
  selectedUser: any = null;

  // Add properties for the dynamic dialog
  displayFileDialog: boolean = false;
  displayType: 'files' | 'pics' = 'files';
  displayItems: any[] = [];

  constructor(
    private appMainService: AppMainService,
    private dynamicTableService: DynamicTableService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private workbookStateService: WorkbookStateService
  ) {
    // Setup scroll debounce
    this.scrollSubject.pipe(
      takeUntil(this.ngUnsubscribe$),
      debounceTime(300)
    ).subscribe(({event, groupValue}) => {
      this.handleGroupScroll(event, groupValue);
    });

    // Setup horizontal scroll debounce
    this.horizontalScrollDebounce.pipe(
      takeUntil(this.ngUnsubscribe$),
      debounceTime(300)
    ).subscribe((scrollLeft) => {
      this.checkHorizontalScroll(scrollLeft);
    });

    // Subscribe to state changes - simplified to just reload
    this.workbookStateService.stateChange$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(change => {
        if (change && change.workbookId === this.workbookId) {
       
          this.pageNumber = 1;
          this.rootPageNumber = 1;
          this.list = [];
          this.getGroupByDynamicTable();
        }
      });
  }

  ngOnInit(): void {
    this.enabledKanbanViewColumns = [...this.kanbanViewColumns.filter(col => col.enable)];
    this.getGroupByDynamicTable();
  }

  ngOnDestroy(): void {
    // Complete and cleanup all subscriptions
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();

    // Complete and cleanup scroll subjects
    this.scrollSubject.complete();
    this.horizontalScrollDebounce.complete();

    // Clear all maps
    this.groupPaginationMap.clear();
    this.isLoadingGroup.clear();
    this.lastScrollPositions.clear();

    // Reset component state
    this.list = [];
    this.isLoading = false;
    this.hasMoreData = false;
    this.hasNextPage = false;
    this.selectedRow = null;
    this.kanbanViewData = null;
    this.totalRecords = 0;
    this.lastHorizontalScrollPosition = 0;

    // Remove any event listeners that might have been added
    const boardWrapper = document.querySelector('.board-wrapper');
    if (boardWrapper) {
      boardWrapper.removeEventListener('scroll', this.onHorizontalScroll);
    }

    // Clear any intervals or timeouts that might be running
    // Note: If you have any setInterval or setTimeout calls, 
    // you should store their IDs and clear them here
  }

  // ngOnChanges(changes: SimpleChanges): void {
  //   if (changes['kanbanViewColumns']) {
  //     this.kanbanViewColumns = [...this.kanbanViewColumns.filter(col => col.enable)];
  //     this.getGroupByDynamicTable();
  //   }
  // }

  onRowSelected(row: any): void {
    this.selectedRow = row;
    this.rowSelected.emit(row);
  }

  async onCardDrop(event: CdkDragDrop<any[]>) {
    if (!event.container.data || !event.previousContainer.data) {
      return;
    }

    const movedCard = event.item.data;
    const targetGroup = event.container.id;
    const sourceGroup = event.previousContainer.id;

    if (!targetGroup || !sourceGroup) {
      return;
    }

    // If it's the same container, just reorder without confirmation
    if (event.previousContainer === event.container) {
      const cards = Array.isArray(event.container.data) ? [...event.container.data] : [];
      const movedIndex = cards.findIndex(card => card === movedCard);
      if (movedIndex > -1) {
        cards.splice(movedIndex, 1);
        cards.splice(event.currentIndex, 0, movedCard);
        event.container.data = cards;
      }
      return;
    }

    // Show confirmation dialog for moving between groups
    this.confirmationService.confirm({
      message: `Are you sure you want to move this data from "${sourceGroup}" to "${targetGroup}"?`,
      header: 'Confirm Data Move',
      icon: 'pi pi-exclamation-triangle',
      key: 'workbookdialog',
      accept: () => {
        // User confirmed the move, proceed with existing logic
        this.moveCard(event, movedCard, targetGroup, sourceGroup);
      },
      reject: () => {
        // User cancelled the move, revert the drag operation
        event.previousContainer.data = [...event.previousContainer.data];
        event.container.data = [...event.container.data];
      }
    });
  }

  private moveCard(event: CdkDragDrop<any[]>, movedCard: any, targetGroup: string, sourceGroup: string) {
    try {
      // Find the source and target columns in the list
      const sourceColumn = this.list.find(col => col.GroupValue === sourceGroup);
      const targetColumn = this.list.find(col => col.GroupValue === targetGroup);

      if (!sourceColumn || !targetColumn) {
        return;
      }

      // Remove from source column
      const sourceIndex = sourceColumn.Data.findIndex(card => card.RowNumber === movedCard.RowNumber);
      if (sourceIndex > -1) {
        sourceColumn.Data.splice(sourceIndex, 1);
        // Decrease source column count
        sourceColumn.GroupCount = (sourceColumn.GroupCount || 0) - 1;
      }

      // Add to target column
      targetColumn.Data.splice(event.currentIndex, 0, movedCard);
      // Increase target column count
      targetColumn.GroupCount = (targetColumn.GroupCount || 0) + 1;

      // Update the cell value in the backend
      if (movedCard?.RowNumber && this.columnName) {
        this.dynamicTableService.updateWorkbookCellValue(
          this.workbookId,
          movedCard.RowNumber,
          this.columnName,
          targetGroup
        ).subscribe({
          next: () => {
            // Find the target group details from steps array
            const targetStep = this.steps.find(step => step.name === targetGroup);
            
            // Emit the card move event with all necessary information
            this.cardMoved.emit({
              card: movedCard,
              newGroup: targetGroup,
              sourceGroup: sourceGroup,
              targetStep: targetStep
            });

            // Force change detection by creating a new array reference
            this.list = [...this.list];
          },
          error: () => {
            // Revert the UI changes if the backend update fails
            if (sourceIndex > -1) {
              sourceColumn.Data.splice(sourceIndex, 0, movedCard);
              // Revert source column count
              sourceColumn.GroupCount = (sourceColumn.GroupCount || 0) + 1;
              targetColumn.Data.splice(event.currentIndex, 1);
              // Revert target column count
              targetColumn.GroupCount = (targetColumn.GroupCount || 0) - 1;
              // Force change detection
              this.list = [...this.list];
            }
          }
        });
      } else {
        // If we don't have the required data, still emit the event
        const targetStep = this.steps.find(step => step.name === targetGroup);
        this.cardMoved.emit({
          card: movedCard,
          newGroup: targetGroup,
          sourceGroup: sourceGroup,
          targetStep: targetStep
        });
      }
    } catch (error) {
      // Revert any UI changes if an error occurs
      const sourceColumn = this.list.find(col => col.GroupValue === sourceGroup);
      const targetColumn = this.list.find(col => col.GroupValue === targetGroup);
      
      if (sourceColumn && targetColumn) {
        const sourceIndex = sourceColumn.Data.findIndex(card => card.RowNumber === movedCard.RowNumber);
        if (sourceIndex > -1) {
          sourceColumn.Data.splice(sourceIndex, 0, movedCard);
          // Revert source column count
          sourceColumn.GroupCount = (sourceColumn.GroupCount || 0) + 1;
          targetColumn.Data.splice(event.currentIndex, 1);
          // Revert target column count
          targetColumn.GroupCount = (targetColumn.GroupCount || 0) - 1;
          // Force change detection
          this.list = [...this.list];
        }
      }
    }
  }
  private kanbanViewData: KanbanViewResponse | null = null;
  private totalRecords: number = 0;

  getGroupByDynamicTable() {
    const groupByQuery: KanbanViewQuery = this.buildGroupByQuery();
    this.appMainService.isShowSpinner('getGroupByDynamicTable');
    this.dynamicTableService.getGroupByDynamicTable(groupByQuery).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: KanbanViewResponse) => {
        this.appMainService.isHideSpinner('getGroupByDynamicTable');
        this.kanbanViewData = response;
        this.hasNextPage = this.kanbanViewData.result.hasNextPage;
        
        if (this.pageNumber === 1) {
          this.list = this.kanbanViewData.result.list;
          // Initialize pagination for each group with total count
          this.list.forEach(group => {
            this.groupPaginationMap.set(group.GroupValue, { 
              pageNumber: 1, 
              hasMore: group.GroupCount > this.rootPageSize,
              totalCount: group.GroupCount,
              loadedCount: this.rootPageSize
            });
            this.isLoadingGroup.set(group.GroupValue, false);
          });
        } else {
          // Append new columns and initialize their pagination
          const newColumns = this.kanbanViewData.result.list;
          this.list = [...this.list, ...newColumns];
          
          // Initialize pagination for new columns only
          newColumns.forEach(group => {
            if (!this.groupPaginationMap.has(group.GroupValue)) {
              this.groupPaginationMap.set(group.GroupValue, { 
                pageNumber: 1, 
                hasMore: group.GroupCount > this.rootPageSize,
                totalCount: group.GroupCount,
                loadedCount: this.rootPageSize
              });
              this.isLoadingGroup.set(group.GroupValue, false);
            }
          });
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.isGrouped = false;
        this.appMainService.isHideSpinner('getGroupByDynamicTable');
        this.isLoading = false;
      }
    });
  }
  // Add these properties to the class
  private rootPageNumber: number = 1;
  private rootPageSize: number = 15;
  private pageSize: number = 15;
  private pageNumber: number = 1;
  private filterInColumns: any[] = [];

  private getEnabledColumnNames(columns: any[]){
    return columns.filter(col => col.enable).map(col => ({ name: col?.name }));
  }

  private getColumnNameByID(id: string): any {
    return this.kanbanViewColumns.find(col => col.id === id);
  }

  buildGroupByQuery(): KanbanViewQuery {
    const selectableColumns = this.getEnabledColumnNames(this.kanbanViewColumns);
    const selectedGroupColumn = this.getColumnNameByID(this.columnId);
    const checkGroupColumnExist = selectableColumns.find(col => col.name === this.columnName);
    if(!checkGroupColumnExist){
       const groupColumn = {
        name: this.columnName,
        dataType: this.selectedGroupByColumn?.fieldDataType 
       }
       selectableColumns.push(groupColumn);
    }
    
    return {
      globalSearchValue: '',
      tableId: this.workbookId,
      pageNumber: this.rootPageNumber || 1,
      pageSize: this.rootPageSize || 15,
      selectableColumns: selectableColumns,
      formControlName: selectedGroupColumn?.formControlName,
      rootPageSize: this.pageSize || 15,
      rootPageNumber: this.pageNumber || 1,
      filterInColumns: this.filterInColumns,
      groupByColumn: { 
        name: this.columnName, 
        dataType: this.selectedGroupByColumn?.fieldDataType 
      }
    };
  }

  async loadMore() {
    if (this.isLoading || !this.hasNextPage) return;
    
    try {
      this.isLoading = true;
      this.pageNumber++;
      await this.getGroupByDynamicTable();
    } catch (error) {
      this.isLoading = false;
    }
  }

  getStepColor(groupValue: string): string {
    const step = this.steps.find(step => step?.name === groupValue);
    return step?.stageColor || step?.color || '#FF0000';
  }

  // Touch event handlers
  onTouchStart(event: TouchEvent) {
    if (event.touches.length === 1) {
      event.preventDefault();
    }
  }

  onTouchMove(event: TouchEvent) {
    if (event.touches.length === 1) {
      event.preventDefault();
    }
  }

  onTouchEnd(event: TouchEvent) {
    event.preventDefault();
  }

  onGroupScroll(event: any, groupValue: string) {
    // Store the current scroll position
    this.lastScrollPositions.set(groupValue, event.target.scrollTop);
    this.scrollSubject.next({event, groupValue});
  }

  private handleGroupScroll(event: any, groupValue: string) {
    const element = event.target;
    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    
    if (atBottom) {
      const groupPagination = this.groupPaginationMap.get(groupValue);
      if (groupPagination && 
          !this.isGroupLoading(groupValue) && 
          groupPagination.hasMore && 
          groupPagination.loadedCount < groupPagination.totalCount) {
        this.loadMoreForGroup(groupValue);
      }
    }
  }

  //Load more for group using filtered API
  async loadMoreForGroup(groupValue: string) {
    const groupPagination = this.groupPaginationMap.get(groupValue);
    const isGroupLoading = this.isLoadingGroup.get(groupValue);

    if (!groupPagination || 
        isGroupLoading || 
        !groupPagination.hasMore || 
        groupPagination.loadedCount >= groupPagination.totalCount) {
      return;
    }

    try {
      this.isLoadingGroup.set(groupValue, true);
      const nextPage = groupPagination.pageNumber + 1;
      const selectableColumns = this.getEnabledColumnNames(this.kanbanViewColumns);
      const selectedGroupColumn = this.getColumnNameByID(this.columnId);
      const checkGroupColumnExist = selectableColumns.find(col => col.name === this.columnName);
      if(!checkGroupColumnExist){
         const groupColumn = {
          name: this.columnName,
          // dataType: this.selectedGroupByColumn?.fieldDataType 
         }
         selectableColumns.push(groupColumn);
      }

      const filterInColumns =  {
        name: this.columnName,
        dataType: selectedGroupColumn?.formControlName,
        operator: "IsEqualTo",
        operationType: "AND",
        values: [
          groupValue
        ]
      }

      const filterQuery = {
        globalSearchValue: '',
        tableId: this.workbookId,
        pageNumber: nextPage,
        pageSize: this.rootPageSize,
        selectableColumns: selectableColumns,
        filterInColumns: [
          ...this.filterInColumns,
          filterInColumns
        ]
      };

      this.appMainService.isShowSpinner('loadMoreForGroup');
      this.dynamicTableService.getFilteredDynamicTable(filterQuery)
        .pipe(takeUntil(this.ngUnsubscribe$))
        .subscribe({
          next: (response: any) => {
            this.appMainService.isHideSpinner('loadMoreForGroup');
            const group = this.list.find(col => col.GroupValue === groupValue);
            if (group && response.result?.list?.length > 0) {
              const newData = response.result.list;
              if (newData.length > 0) {
                group.Data = [...group.Data, ...newData];
                const newLoadedCount = groupPagination.loadedCount + newData.length;
                
                this.groupPaginationMap.set(groupValue, { 
                  pageNumber: nextPage, 
                  hasMore: newLoadedCount < groupPagination.totalCount,
                  totalCount: groupPagination.totalCount,
                  loadedCount: newLoadedCount
                });
                
                // Force change detection
                this.list = [...this.list];
              }
            }
            else{
              this.resetScrollPosition(groupValue);
            }
            this.isLoadingGroup.set(groupValue, false);
          },
          error: (error) => {
            this.appMainService.isHideSpinner('loadMoreForGroup');
            this.isLoadingGroup.set(groupValue, false);
            this.resetScrollPosition(groupValue);
          }
        });
    } catch (error) {
      this.isLoadingGroup.set(groupValue, false);
      this.resetScrollPosition(groupValue);
    }
  }

  resetScrollPosition(groupValue: string) {
    const groupElement = document.querySelector(`[id="${groupValue}"] .kanban-opportunity-tasks-container`) as HTMLElement;
    if (groupElement) {
      // Get the last known good scroll position or default to 0
      const lastPosition = this.lastScrollPositions.get(groupValue) || 0;
      // Reset scroll position with a small delay to ensure DOM is updated
      setTimeout(() => {
        groupElement.scrollTop = lastPosition;
      }, 0);
    }
  }
  // Modified method to check if a group has more data
  hasMoreDataForGroup(groupValue: string): boolean {
    const groupPagination = this.groupPaginationMap.get(groupValue);
    return (groupPagination?.hasMore && groupPagination.loadedCount < groupPagination.totalCount) ?? false;
  }

  // Method to check if a group is currently loading
  isGroupLoading(groupValue: string): boolean {
    return this.isLoadingGroup.get(groupValue) ?? false;
  }

  // Add horizontal scroll handler
  onHorizontalScroll(event: any) {
    if (this.hasNextPage && !this.isLoading) {
      this.horizontalScrollDebounce.next(event.target.scrollLeft);
    }
  }

  // Check if we need to load more columns
  private checkHorizontalScroll(scrollLeft: number) {
    const boardWrapper = document.querySelector('.board-wrapper');
    if (!boardWrapper) return;

    const scrollWidth = boardWrapper.scrollWidth;
    const clientWidth = boardWrapper.clientWidth;

    // If we're near the right edge (within 100px) and there's more data
    if (scrollWidth - (scrollLeft + clientWidth) < 100 && this.hasNextPage && !this.isLoading) {
        this.loadMore();
    }

    this.lastHorizontalScrollPosition = scrollLeft;
  }

  /**
   * Safely parses JSON values and formats them based on form control type
   */
  safeJsonParse(value: any): any {
    try {
      return JSON.parse(value);
    } catch (e) {
      return value; // Return as is if parsing fails
    }
  }

  // File handling methods
  openFiles(event: any, files: any[]) {
    if (event) {
      event.stopPropagation();
    }
    this.displayType = 'files';
    this.displayItems = files;
    this.displayFileDialog = true;
  }

  openPics(event: any, pics: any[]) {
    if (event) {
      event.stopPropagation();
    }
    this.displayType = 'pics';
    this.displayItems = pics;
    this.displayFileDialog = true;
  }

  onDisplayDialogChange(displayFileDialog: boolean) {
    this.displayFileDialog = false;
    this.displayItems = [];
  }
  // User details methods
  openUserDetails(event: any, user: any) {
    if (event) {
      event.stopPropagation();
    }
    this.selectedUser = user;
    this.displayUserDetails = true;
  }

  closeUserDetails() {
    this.displayUserDetails = false;
    this.selectedUser = null;
  }
} 